import "../../../Css/Common/Modals/CompleteInsightModal.css";
import React, { useState } from "react";
import { GlassModalHeader } from "./GlassModalHeader";
import CustomDropdown from "../CustomDropDown";
import { updateInsightStatus } from "../../../Api/updateInsightStatus";
import { publishUserEvent } from "../../../Api/publishUserEvent";
import { INSIGHT_CATEGORY, INSIGHT_STATUS } from "../../../Types/account";
import { InsightCompletedEvent, USER_EVENT_TYPE } from "../../../Types/userEvents";
import { getUserFromLS } from "../../../utils/getUserFromLS";
import { mapInsightCategoryToType } from "../../../utils/mapInsightCategoryToType";
import { resolveUserEventClientId } from "../../../utils/userEventClientId";

type CompleteInsightModalProps = {
  insightId: number;
  accountId: string | null;
  clientId: string | null;
  industryId?: string | null;
  insightType: INSIGHT_CATEGORY;
  onClose: () => void;
  onComplete: (insightId: number) => void;
};

const COUNT_OPTIONS = [
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5+", label: "5+" },
];

export const CompleteInsightModal: React.FC<CompleteInsightModalProps> = ({
  insightId,
  accountId,
  clientId,
  industryId = null,
  insightType,
  onClose,
  onComplete,
}) => {
  const user = getUserFromLS();
  const [numEmails, setNumEmails] = useState("0");
  const [numCalls, setNumCalls] = useState("0");
  const [numLinkedin, setNumLinkedIn] = useState("0");
  const [comments, setComments] = useState("");

  const createEventId = () => {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const publishInsightCompletedEvent = () => {
    const event: InsightCompletedEvent = {
      eventId: createEventId(),
      eventType: USER_EVENT_TYPE.INSIGHT_COMPLETED,
      userId: user?.id ?? null,
      accountId,
      clientId: resolveUserEventClientId(clientId, user?.client_id) ?? "",
      contactId: null,
      industryId: insightType === INSIGHT_CATEGORY.INDUSTRY ? industryId : null,
      payload: {
        insightId,
        insightCategory: insightType,
      },
      metadata: {
        source_service: "intentico-react-app",
        version: null,
      },
      occurredAt: new Date(),
    };

    publishUserEvent(event, undefined, (error) => {
      console.warn("Failed to publish insight completed event", error);
    });
  };

  const handleComplete = () => {
    const params = {
      insight_type: mapInsightCategoryToType(insightType),
      insight_id: insightId,
      new_status: INSIGHT_STATUS.COMPLETED,
      comments: comments.trim() || null,
      number_of_emails: parseInt(numEmails, 10),
      number_of_calls: parseInt(numCalls, 10),
      number_of_linkedin_dms: parseInt(numLinkedin, 10),
      account_id: accountId,
    };

    updateInsightStatus(
      params,
      () => {
        publishInsightCompletedEvent();
        onComplete(insightId);
      },
      () => {}
    );
  };

  return (
    <div className="completeInsightModal_container glassModal_shell">
      <GlassModalHeader
        kicker="Insight"
        title="Complete insight"
        subtitle="Log outreach activity, then mark this insight complete."
        onClose={onClose}
        closeAriaLabel="Close complete insight"
      />
      <div className="completeInsightModal_contentContainer">
        <div className="completeInsightModal_infoCallout" role="note">
          <div className="completeInsightModal_infoIconWrap" aria-hidden>
            <span className="completeInsightModal_infoIcon fa-solid fa-circle-info" />
          </div>
          <p className="completeInsightModal_infoText">
            This insight will leave your homepage timeline but stay available on
            account and industry pages.
          </p>
        </div>

        <div className="completeInsightModal_fields">
          <div className="completeInsightModal_field">
            <span className="completeInsightModal_fieldLabel">Emails sent</span>
            <div className="completeInsightModal_dropdown">
              <CustomDropdown
                options={COUNT_OPTIONS}
                onChange={setNumEmails}
                portaledMenu
              />
            </div>
          </div>
          <div className="completeInsightModal_field">
            <span className="completeInsightModal_fieldLabel">Calls made</span>
            <div className="completeInsightModal_dropdown">
              <CustomDropdown
                options={COUNT_OPTIONS}
                onChange={setNumCalls}
                portaledMenu
              />
            </div>
          </div>
          <div className="completeInsightModal_field">
            <span className="completeInsightModal_fieldLabel">
              LinkedIn posts / DMs
            </span>
            <div className="completeInsightModal_dropdown">
              <CustomDropdown
                options={COUNT_OPTIONS}
                onChange={setNumLinkedIn}
                portaledMenu
              />
            </div>
          </div>
        </div>

        <div className="completeInsightModal_commentsBlock">
          <label
            className="completeInsightModal_fieldLabel"
            htmlFor="complete-insight-comments"
          >
            Additional comments
          </label>
          <textarea
            id="complete-insight-comments"
            className="completeInsightModal_commentsTextArea"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Optional notes about this outreach…"
          />
        </div>

        <div className="completeInsightModal_footerButtonRow">
          <button
            type="button"
            className="completeInsightModal_cancelButton"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="completeInsightModal_primaryButton completeInsightModal_primaryButton--complete"
            onClick={handleComplete}
          >
            <i className="fa-duotone fa-circle-check" aria-hidden />
            Complete
          </button>
        </div>
      </div>
    </div>
  );
};
